import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlineAcademicCap,
  HiOutlineBookOpen,
  HiOutlineQuestionMarkCircle,
  HiOutlineClipboardList,
  HiOutlineNewspaper,
  HiOutlineBriefcase,
  HiOutlineBell,
  HiOutlineMicrophone,
  HiOutlineUserGroup,
  HiOutlineScale,
  HiOutlineChevronLeft,
  HiOutlineLogout,
  HiOutlineCog
} from 'react-icons/hi';
import type { MenuSection } from '@/types';
import logo from './../../assets/logo.svg';

interface MenuItem {
  path: string;
  icon: React.ComponentType;
  label: string;
  adminOnly?: boolean;
}

interface MenuSectionWithAdmin {
  section: string;
  adminOnly?: boolean;
  items: MenuItem[];
}

const allMenuItems: MenuSectionWithAdmin[] = [
  {
    section: 'Principal',
    adminOnly: true,
    items: [
      { path: '/usuarios', icon: HiOutlineUsers, label: 'Usuarios' },
    ]
  },
  {
    section: 'Capacitación',
    items: [
      { path: '/dashboard-capacitaciones', icon: HiOutlineAcademicCap, label: 'Dashboard Capacitaciones' },
      { path: '/capacitacion', icon: HiOutlineBookOpen, label: 'Capacitación', adminOnly: true },
      { path: '/biblioteca', icon: HiOutlineBookOpen, label: 'Biblioteca', adminOnly: true },
      { path: '/cuestionarios', icon: HiOutlineClipboardList, label: 'Cuestionarios', adminOnly: true },
    ]
  },
];

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

function Sidebar({ 
  isCollapsed, 
  onToggle, 
  isMobileOpen, 
  onMobileClose 
}: SidebarProps): JSX.Element {
  const { user, logout, isFullAdmin } = useAuth();

  const menuItems = useMemo(() => {
    return allMenuItems
      .filter((section) => !section.adminOnly || isFullAdmin)
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.adminOnly || isFullAdmin),
      }))
      .filter((section) => section.items.length > 0);
  }, [isFullAdmin]);

  const handleLogout = async (): Promise<void> => {
    await logout();
  };

  const handleNavClick = (): void => {
    if (window.innerWidth < 992) {
      onMobileClose();
    }
  };

  return (
    <>
      <div 
        className={`sidebar-overlay ${isMobileOpen ? 'show' : ''}`}
        onClick={onMobileClose}
        role="button"
        tabIndex={-1}
        aria-label="Cerrar menú"
        onKeyDown={(e) => e.key === 'Escape' && onMobileClose()}
      />

      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src={logo} className='img-fluid' style={{width:'120px'}} />
          </div>
          <button 
            className="sidebar-toggle"
            onClick={onToggle}
            aria-label={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <HiOutlineChevronLeft />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="sidebar-section">
              <div className="sidebar-section-title">{section.section}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => 
                    `sidebar-nav-item ${isActive ? 'active' : ''}`
                  }
                  onClick={handleNavClick}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className="sidebar-nav-item-icon">
                    <item.icon />
                  </span>
                  <span className="sidebar-nav-item-text">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">
              {user?.name?.charAt(0) ?? 'U'}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name ?? 'Usuario'}</div>
              <div className="sidebar-user-role">{user?.role ?? 'Rol'}</div>
            </div>
          </div>
          
          {!isCollapsed && (
            <div className="d-flex gap-2 mt-3">
              <NavLink 
                to="/configuracion" 
                className="btn btn-ghost btn-sm flex-grow-1"
                onClick={handleNavClick}
              >
                <HiOutlineCog />
                <span>Ajustes</span>
              </NavLink>
              <button 
                className="btn btn-ghost btn-sm flex-grow-1"
                onClick={() => void handleLogout()}
              >
                <HiOutlineLogout />
                <span>Salir</span>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default Sidebar;